/**
 * Editor de `subTableKind` y columnas (`subTableConfig`) para subsecciones dinámicas del formato (modelo).
 * Los kinds y plantillas por defecto provienen de `incidentSubTable.ts`, mantenido en tres copias
 * idénticas (stem-web-backend, planiwise-frontend, planiwise-app); cualquier kind o columna nueva
 * debe existir en las tres antes de usarla en listas, PDF o agregación a incidente.
 */

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Paper,
  Typography,
} from '@mui/material';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useFieldArray, useFormContext, useWatch} from 'react-hook-form';
import {useSelector} from 'react-redux';
import authSelectors from 'src/modules/auth/authSelectors';
import {
  buildDefaultSubTableConfigForIncidentSubTable,
  getIncidentSubTableDef,
  isIncidentSubTableFormatSubsection,
  type IncidentSubTableDef,
  type SubTableFieldConfig,
} from 'src/modules/incident/incidentSubTable';
import InputFormItem from 'src/view/shared/form/items/InputFormItem';
import SwitchFormItem from 'src/view/shared/form/items/SwitchFormItem';

/** Pasar a false para ofrecer cargo, fecha y evidencia de medidas en todos los tenants. */
const hideBlumarStuffForOthers = true;
const BLUMAR_SSO_TENANT_ID = '62a8b6dd2323505723b7e92b';

type Props = {
  subsectionName: string;
};

function getColumnOrder(def: IncidentSubTableDef | undefined, columnId: string): number {
  if (!def?.columnTemplates?.length) return 999;
  const i = def.columnTemplates.findIndex((c) => c.id === columnId);
  return i === -1 ? 999 : i;
}

function createFieldForColumn(def: IncidentSubTableDef, columnId: string): SubTableFieldConfig {
  const tmpl = def.columnTemplates.find((t) => t.id === columnId);
  const label = tmpl?.defaultLabel ?? columnId;
  const keyPointer = tmpl?.keyPointer ?? columnId;
  return {
    columnId,
    label,
    keyPointer,
    responseType: tmpl?.responseType || 'textField',
    ...(tmpl?.supportsPhotos ? {havePhotos: true} : {}),
  };
}

export default function IncidentSubTableConfigForm({subsectionName}: Props) {
  const {control, setValue, getValues} = useFormContext();
  const currentTenant = useSelector(authSelectors.selectCurrentTenant);
  const showBlumarIncidentFields =
    !hideBlumarStuffForOthers || currentTenant?.id === BLUMAR_SSO_TENANT_ID;
  const configPath = `${subsectionName}.subTableConfig`;
  const fieldsPath = `${configPath}.fields`;

  const {fields, insert, remove, replace} = useFieldArray({
    control,
    name: fieldsPath,
  });

  const fieldValues = useWatch({control, name: fieldsPath, defaultValue: []}) as any[];
  const subsectionWatch = useWatch({control, name: subsectionName}) as any;
  const subTableDef = useMemo(
    () => getIncidentSubTableDef(String(subsectionWatch?.subTableKind || '')),
    [subsectionWatch?.subTableKind],
  );
  const columnOptions = useMemo(
    () =>
      (subTableDef?.columnTemplates ?? [])
        .filter((t) => showBlumarIncidentFields || !t.blumarOnly)
        .map((t) => ({
          value: t.id,
          label: t.defaultLabel,
        })),
    [subTableDef, showBlumarIncidentFields],
  );

  const selectedColumns = new Set((fieldValues || []).map((f) => f?.columnId).filter(Boolean) as string[]);
  const [expandedAccordion, setExpandedAccordion] = useState<number | false>(false);

  useEffect(() => {
    if (!subTableDef?.hasColumnConfigEditor) return;
    if (!isIncidentSubTableFormatSubsection(subsectionWatch)) return;
    const currentFields = getValues(`${configPath}.fields`) ?? [];
    if (currentFields.length > 0) return;
    const kind = String(subsectionWatch?.subTableKind || '');
    const defaults = buildDefaultSubTableConfigForIncidentSubTable(kind);
    if (!defaults) return;
    setValue(configPath, {...defaults}, {shouldDirty: true});
    replace(defaults.fields);
  }, [subsectionWatch, subTableDef, getValues, setValue, configPath, replace]);

  const sortedFieldValues = useMemo(() => {
    const list = [...(fieldValues || [])];
    return list.sort((a, b) => getColumnOrder(subTableDef, a?.columnId) - getColumnOrder(subTableDef, b?.columnId));
  }, [fieldValues, subTableDef]);

  useEffect(() => {
    if (!fieldValues?.length) return;
    const currentOrder = (fieldValues || []).map((f) => f?.columnId).join(',');
    const correctOrder = sortedFieldValues.map((f) => f?.columnId).join(',');
    if (currentOrder !== correctOrder) {
      replace(sortedFieldValues);
    }
  }, [fieldValues, sortedFieldValues, replace]);

  const handleToggleColumn = useCallback(
    (columnId: string, checked: boolean) => {
      if (!subTableDef) return;
      if (checked) {
        const existing = fieldValues?.find((f) => f?.columnId === columnId);
        if (existing) return;
        const newField = createFieldForColumn(subTableDef, columnId);
        const order = getColumnOrder(subTableDef, columnId);
        let insertIndex = (fieldValues || []).length;
        for (let i = 0; i < (fieldValues || []).length; i++) {
          if (getColumnOrder(subTableDef, fieldValues[i]?.columnId) > order) {
            insertIndex = i;
            break;
          }
        }
        insert(insertIndex, newField);
        setExpandedAccordion(insertIndex);
      } else {
        const idx = fieldValues?.findIndex((f) => f?.columnId === columnId);
        if (typeof idx === 'number' && idx >= 0) {
          remove(idx);
          setExpandedAccordion(false);
        }
      }
    },
    [fieldValues, insert, remove, subTableDef],
  );

  const handleAccordionChange = (index: number) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordion(isExpanded ? index : false);
  };

  const editorTitle = subTableDef?.editorLabel ?? 'Tabla de subsección (incidente)';

  return (
    <Box sx={{border: '1px dashed rgba(0,0,0,0.3)', borderRadius: 1.5, p: 2}}>
      <Box sx={{mb: 2}}>
        <Typography variant="h6" sx={{fontSize: 16}}>
          {editorTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
          Elige qué columnas incluye cada fila.
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{mt: 1, p: 2}}>
        <Typography variant="subtitle2" sx={{mb: 1.5}}>
          Campos a incluir
        </Typography>
        <FormGroup
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 0.5,
          }}>
          {columnOptions.map((opt) => (
            <FormControlLabel
              key={opt.value}
              control={
                <Checkbox
                  checked={selectedColumns.has(opt.value)}
                  onChange={(e) => handleToggleColumn(opt.value, e.target.checked)}
                  size="small"
                />
              }
              label={opt.label}
            />
          ))}
        </FormGroup>
      </Paper>

      {fields.map((field, index) => {
        const fieldData = fieldValues?.[index];
        const columnId = fieldData?.columnId as string;
        const fieldLabel = columnOptions.find((o) => o.value === columnId)?.label ?? columnId;
        const columnSupportsPhotos = subTableDef?.columnTemplates.some((c) => c.id === columnId && c.supportsPhotos);

        return (
          <Accordion
            key={field.id}
            expanded={expandedAccordion === index}
            onChange={handleAccordionChange(index)}
            disableGutters
            sx={{
              mt: 1.5,
              boxShadow: '0px 1px 3px rgba(0,0,0,0.08)',
              '&:before': {display: 'none'},
            }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                minHeight: 48,
                '&.Mui-expanded': {minHeight: 48},
                '& .MuiAccordionSummary-content': {my: 1},
              }}>
              <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                {fieldLabel}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{pt: 0, pb: 2}}>
              <Box sx={{pl: 0.5, maxWidth: 480}}>
                <Typography variant="overline" sx={{opacity: 0.8, fontSize: 11}}>
                  Texto de la pregunta
                </Typography>
                <Box sx={{mt: 1}}>
                  <InputFormItem name={`${fieldsPath}.${index}.label`} label="Título" required />
                </Box>
                {columnSupportsPhotos && (
                  <Box sx={{mt: 2}}>
                    <SwitchFormItem
                      name={`${fieldsPath}.${index}.havePhotos`}
                      label="Permitir fotos adjuntas en la declaración"
                    />
                  </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
